import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { UI } from '../ui';
import { ChipMultiSelectComponent } from './chip-multi-select.component';
import { ColorFieldComponent } from './color-field.component';
import {
  APPEARANCE_COLOR_GROUPS,
  APPEARANCE_LENGTH_KEYS,
  APPEARANCE_STYLE_KEYS,
  BILLING_TYPES,
  BOOLEAN_KEYS,
  BORDER_STYLES,
  BUTTON_SHAPES,
  BUTTON_TYPES,
  CARD_NETWORKS,
  DESCRIPTOR_NAME_MAX,
  PAYMENT_TYPES,
  SUB_MERCHANT_FIELDS,
  THEMES,
  cleanCardSettings,
  validateCardSettings,
  type CardCheckoutSettings,
  type SubMerchantKey,
} from './merchant-config.models';

/** Structured editor for `Merchant.card_checkout_settings` (no JSON). Emits a cleaned object. */
@Component({
  selector: 'mm-card-checkout-config',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ChipMultiSelectComponent, ColorFieldComponent, ...UI],
  template: `
    <p class="mb-3 text-[0.8125rem] text-muted">
      These preferences only narrow what the card provider allows; leave a field empty to use the provider default.
    </p>

    <h3 class="m-0 mb-1.5 text-sm font-bold text-fg">Payment types</h3>
    <mm-chip-multi-select label="Payment types" [options]="paymentTypes" [disabled]="disabled()"
      [value]="value().payment_types ?? []" (valueChange)="set('payment_types', $event)" />

    <h3 class="mx-0 mb-1.5 mt-4 text-sm font-bold text-fg">Card networks</h3>
    <mm-chip-multi-select label="Card networks" [options]="networks" [disabled]="disabled()"
      [value]="value().card_networks ?? []" (valueChange)="set('card_networks', $event)" />

    <div class="mt-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
      <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Button label</span>
        <select uiInput [ngModel]="value().button_type ?? ''" (ngModelChange)="set('button_type', $event)" [disabled]="disabled()">
          <option value="">Provider default</option>
          @for (o of buttonTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
        </select>
      </label>
      <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Billing details</span>
        <select uiInput [ngModel]="value().billing_type ?? ''" (ngModelChange)="set('billing_type', $event)" [disabled]="disabled()">
          <option value="">Provider default</option>
          @for (o of billingTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
        </select>
      </label>
      <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Theme</span>
        <select uiInput [ngModel]="value().theme ?? ''" (ngModelChange)="set('theme', $event)" [disabled]="disabled()">
          <option value="">Provider default</option>
          @for (o of themes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
        </select>
      </label>
      <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Statement descriptor</span>
        <input uiInput [attr.maxlength]="descriptorMax" [ngModel]="value().descriptor_name ?? ''"
          (ngModelChange)="set('descriptor_name', $event)" [disabled]="disabled()" />
      </label>
    </div>

    <div class="mt-4 flex flex-col gap-3">
      @for (toggle of booleans; track toggle.key) {
        <ui-switch [checked]="!!value()[toggle.key]" [disabled]="disabled()" (changed)="set(toggle.key, $event)">
          {{ toggle.label }}<span description>{{ toggle.hint }}</span>
        </ui-switch>
      }
    </div>

    <ui-accordion class="mt-4" [multi]="true">
    <ui-accordion-item title="Sub-merchant (payment facilitators)">
      <div class="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        @for (f of subFields; track f.key) {
          <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
            <span>{{ f.label }}</span>
            <input uiInput [attr.maxlength]="f.max" [ngModel]="value().sub_merchant?.[f.key] ?? ''"
              (ngModelChange)="setSub(f.key, $event)" [disabled]="disabled()" />
          </label>
        }
      </div>
    </ui-accordion-item>

    <ui-accordion-item title="Appearance overrides">
      <p class="mb-3 text-xs text-muted">Only set what must differ from the checkout colours; empty fields are not sent.</p>
      <div class="mb-4 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
          <span>Button shape</span>
          <select uiInput [ngModel]="override('buttonShape')" (ngModelChange)="setOverride('buttonShape', $event)" [disabled]="disabled()">
            <option value="">Default</option>
            @for (s of shapes; track s) { <option [value]="s">{{ s }}</option> }
          </select>
        </label>
        @for (key of styleKeys; track key) {
          <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
            <span>{{ key }}</span>
            <select uiInput [ngModel]="override(key)" (ngModelChange)="setOverride(key, $event)" [disabled]="disabled()">
              <option value="">Default</option>
              @for (s of borderStyles; track s) { <option [value]="s">{{ s }}</option> }
            </select>
          </label>
        }
        @for (key of lengthKeys; track key) {
          <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
            <span>{{ key }}</span>
            <input uiInput placeholder="8px" [ngModel]="override(key)" (ngModelChange)="setOverride(key, $event)" [disabled]="disabled()" />
          </label>
        }
        <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
          <span>fontFamily</span>
          <input uiInput placeholder="Inter, Arial, sans-serif" [ngModel]="override('fontFamily')"
            (ngModelChange)="setOverride('fontFamily', $event)" [disabled]="disabled()" />
        </label>
      </div>
      @for (group of colorGroups; track group.title) {
        <h4 class="mx-0 mb-2 mt-3 text-[0.8125rem] font-bold text-fg">{{ group.title }}</h4>
        <div class="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
          @for (key of group.keys; track key) {
            <mm-color-field [label]="key" [optional]="true" [disabled]="disabled()"
              [value]="override(key)" (valueChange)="setOverride(key, $event)" />
          }
        </div>
      }
    </ui-accordion-item>
    </ui-accordion>

    @if (problems().length) {
      <ul class="mb-0 mt-3 list-disc pl-5 text-[0.8125rem] text-danger">
        @for (p of problems(); track p) { <li>{{ p }}</li> }
      </ul>
    }
  `,
})
export class CardCheckoutConfigComponent {
  readonly value = model.required<CardCheckoutSettings>();
  readonly disabled = input(false);

  readonly paymentTypes = PAYMENT_TYPES;
  readonly networks = CARD_NETWORKS;
  readonly buttonTypes = BUTTON_TYPES;
  readonly billingTypes = BILLING_TYPES;
  readonly themes = THEMES;
  readonly booleans = BOOLEAN_KEYS;
  readonly subFields = SUB_MERCHANT_FIELDS;
  readonly descriptorMax = DESCRIPTOR_NAME_MAX;
  readonly colorGroups = APPEARANCE_COLOR_GROUPS;
  readonly lengthKeys = APPEARANCE_LENGTH_KEYS;
  readonly styleKeys = APPEARANCE_STYLE_KEYS;
  readonly borderStyles = BORDER_STYLES;
  readonly shapes = BUTTON_SHAPES;

  readonly problems = computed(() => validateCardSettings(this.value()));

  set<K extends keyof CardCheckoutSettings>(key: K, next: CardCheckoutSettings[K] | ''): void {
    this.value.update((current) => cleanCardSettings({ ...current, [key]: next }));
  }

  setSub(key: SubMerchantKey, text: string): void {
    this.set('sub_merchant', { ...this.value().sub_merchant, [key]: text });
  }

  override(key: string): string {
    return this.value().appearance_overrides?.[key] ?? '';
  }

  setOverride(key: string, text: string): void {
    this.set('appearance_overrides', { ...this.value().appearance_overrides, [key]: text });
  }
}
